/**
 * Static registry of lucide-react components used by the app.
 *
 * Static named imports let Vite/Rollup tree-shake away the rest of
 * `lucide-react` (which ships 1000+ icons). The runtime resolver in
 * `Icon.tsx` looks names up in this map rather than indexing the
 * lucide-react namespace dynamically.
 *
 * Adding a new icon: import it and add an entry below. Entries are sorted
 * alphabetically by kebab key.
 */
import {
  Activity,
  Banknote,
  Bath,
  BedDouble,
  Bell,
  Bike,
  Bone,
  Book,
  BookOpen,
  Brain,
  Briefcase,
  BriefcaseBusiness,
  Building,
  Bus,
  Cake,
  Calendar,
  Camera,
  Car,
  Cat,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cloud,
  CloudOff,
  Coffee,
  Coins,
  Cookie,
  CreditCard,
  CupSoda,
  Disc,
  Dog,
  Droplets,
  Dumbbell,
  Film,
  Filter,
  Fish,
  Flame,
  Fuel,
  Gamepad,
  Gamepad2,
  Gift,
  Globe,
  GraduationCap,
  Hash,
  Headphones,
  Heart,
  HeartPulse,
  Home,
  Hospital,
  House,
  IceCream,
  Landmark,
  Laptop,
  Lightbulb,
  LineChart,
  Lock,
  Luggage,
  Map,
  MapPin,
  Minus,
  MoreHorizontal,
  Mountain,
  Music,
  PaintRoller,
  ParkingCircle,
  PawPrint,
  Pencil,
  Percent,
  Phone,
  PieChart,
  PiggyBank,
  Pill,
  Pizza,
  Plane,
  PlaneTakeoff,
  Plug,
  Plus,
  Receipt,
  RefreshCw,
  Repeat,
  Salad,
  Sandwich,
  ScanFace,
  Scissors,
  Search,
  Settings,
  Shirt,
  ShoppingBag,
  Smartphone,
  Smile,
  Sofa,
  Sparkles,
  Star,
  Stethoscope,
  StickyNote,
  Sun,
  Tag,
  Tent,
  Ticket,
  Train,
  TramFront,
  Trash2,
  TrendingDown,
  TrendingUp,
  Tv,
  User,
  Utensils,
  UtensilsCrossed,
  Wallet,
  Wifi,
  Wine,
  Wrench,
  X,
} from 'lucide-react';

import type { ComponentType, SVGProps } from 'react';

export type LucideComponent = ComponentType<
  SVGProps<SVGSVGElement> & { size?: number; strokeWidth?: number }
>;

export const LUCIDE_REGISTRY: Readonly<Record<string, LucideComponent>> = {
  'activity': Activity as LucideComponent,
  'banknote': Banknote as LucideComponent,
  'bath': Bath as LucideComponent,
  'bed-double': BedDouble as LucideComponent,
  'bell': Bell as LucideComponent,
  'bike': Bike as LucideComponent,
  'bone': Bone as LucideComponent,
  'book': Book as LucideComponent,
  'book-open': BookOpen as LucideComponent,
  'brain': Brain as LucideComponent,
  'briefcase': Briefcase as LucideComponent,
  'briefcase-business': BriefcaseBusiness as LucideComponent,
  'building': Building as LucideComponent,
  'bus': Bus as LucideComponent,
  'cake': Cake as LucideComponent,
  'calendar': Calendar as LucideComponent,
  'camera': Camera as LucideComponent,
  'car': Car as LucideComponent,
  'cat': Cat as LucideComponent,
  'check': Check as LucideComponent,
  'chevron-down': ChevronDown as LucideComponent,
  'chevron-left': ChevronLeft as LucideComponent,
  'chevron-right': ChevronRight as LucideComponent,
  'clock': Clock as LucideComponent,
  'cloud': Cloud as LucideComponent,
  'cloud-off': CloudOff as LucideComponent,
  'coffee': Coffee as LucideComponent,
  'coins': Coins as LucideComponent,
  'cookie': Cookie as LucideComponent,
  'credit-card': CreditCard as LucideComponent,
  'cup-soda': CupSoda as LucideComponent,
  'disc': Disc as LucideComponent,
  'dog': Dog as LucideComponent,
  'droplets': Droplets as LucideComponent,
  'dumbbell': Dumbbell as LucideComponent,
  'film': Film as LucideComponent,
  'filter': Filter as LucideComponent,
  'fish': Fish as LucideComponent,
  'flame': Flame as LucideComponent,
  'fuel': Fuel as LucideComponent,
  'gamepad': Gamepad as LucideComponent,
  'gamepad-2': Gamepad2 as LucideComponent,
  'gift': Gift as LucideComponent,
  'globe': Globe as LucideComponent,
  'graduation-cap': GraduationCap as LucideComponent,
  'hash': Hash as LucideComponent,
  'headphones': Headphones as LucideComponent,
  'heart': Heart as LucideComponent,
  'heart-pulse': HeartPulse as LucideComponent,
  'home': Home as LucideComponent,
  'hospital': Hospital as LucideComponent,
  'house': House as LucideComponent,
  'ice-cream': IceCream as LucideComponent,
  'landmark': Landmark as LucideComponent,
  'laptop': Laptop as LucideComponent,
  'lightbulb': Lightbulb as LucideComponent,
  'line-chart': LineChart as LucideComponent,
  'lock': Lock as LucideComponent,
  'luggage': Luggage as LucideComponent,
  'map': Map as LucideComponent,
  'map-pin': MapPin as LucideComponent,
  'minus': Minus as LucideComponent,
  'more-horizontal': MoreHorizontal as LucideComponent,
  'mountain': Mountain as LucideComponent,
  'music': Music as LucideComponent,
  'paint-roller': PaintRoller as LucideComponent,
  'parking-circle': ParkingCircle as LucideComponent,
  'paw-print': PawPrint as LucideComponent,
  'pencil': Pencil as LucideComponent,
  'percent': Percent as LucideComponent,
  'phone': Phone as LucideComponent,
  'pie-chart': PieChart as LucideComponent,
  'piggy-bank': PiggyBank as LucideComponent,
  'pill': Pill as LucideComponent,
  'pizza': Pizza as LucideComponent,
  'plane': Plane as LucideComponent,
  'plane-takeoff': PlaneTakeoff as LucideComponent,
  'plug': Plug as LucideComponent,
  'plus': Plus as LucideComponent,
  'receipt': Receipt as LucideComponent,
  'refresh-cw': RefreshCw as LucideComponent,
  'repeat': Repeat as LucideComponent,
  'salad': Salad as LucideComponent,
  'sandwich': Sandwich as LucideComponent,
  'scan-face': ScanFace as LucideComponent,
  'scissors': Scissors as LucideComponent,
  'search': Search as LucideComponent,
  'settings': Settings as LucideComponent,
  'shirt': Shirt as LucideComponent,
  'shopping-bag': ShoppingBag as LucideComponent,
  'smartphone': Smartphone as LucideComponent,
  'smile': Smile as LucideComponent,
  'sofa': Sofa as LucideComponent,
  'sparkles': Sparkles as LucideComponent,
  'star': Star as LucideComponent,
  'stethoscope': Stethoscope as LucideComponent,
  'sticky-note': StickyNote as LucideComponent,
  'sun': Sun as LucideComponent,
  'tag': Tag as LucideComponent,
  'tent': Tent as LucideComponent,
  'ticket': Ticket as LucideComponent,
  'train': Train as LucideComponent,
  'tram-front': TramFront as LucideComponent,
  'trash-2': Trash2 as LucideComponent,
  'trending-down': TrendingDown as LucideComponent,
  'trending-up': TrendingUp as LucideComponent,
  'tv': Tv as LucideComponent,
  'user': User as LucideComponent,
  'utensils': Utensils as LucideComponent,
  'utensils-crossed': UtensilsCrossed as LucideComponent,
  'wallet': Wallet as LucideComponent,
  'wifi': Wifi as LucideComponent,
  'wine': Wine as LucideComponent,
  'wrench': Wrench as LucideComponent,
  'x': X as LucideComponent,
};

export const TAG_FALLBACK: LucideComponent = Tag as LucideComponent;
